import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { IRegEdIntegrationStatus } from './types';

export const REGED_INTEGRATION_QUERY_KEY = 'regEdIntegration';

export const regEdIntegrationQueryKey = (accountId?: string) => [REGED_INTEGRATION_QUERY_KEY, accountId] as const;

/**
 * The API returns a synthesized empty record (not a 404) when no integration exists, so
 * `configurationStatus.needsConfiguration` drives the connect flow. Note configured ≠ enabled.
 */
export const useRegEdIntegrationQuery = ({
	enabled = true,
	impersonationContext,
}: {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
} = {}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryKey: regEdIntegrationQueryKey(impersonationContext?.account?.id),
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IRegEdIntegrationStatus>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'compliance/regEd/integration',
				}),
				'GET'
			),
		meta: {
			toastErrorMessage: 'Failed to load the RegEd integration.',
		},
	});
};

/** Both mutations return the updated status; writing it first spares the tab a frame of the pre-mutation value. */
export const setRegEdIntegration = (accountId: string | undefined, status: IRegEdIntegrationStatus) => {
	reactQueryClient.setQueryData(regEdIntegrationQueryKey(accountId), status);
};

export const invalidateRegEdIntegration = () => {
	reactQueryClient.invalidateQueries({ queryKey: [REGED_INTEGRATION_QUERY_KEY] });
};
